import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import NotFound from "../notFound/NotFound";
import { getProjectBySlug, projects } from "../../data/projects";

export default function ProjectDetails() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  useEffect(() => {
    if (!project) return;
    document.title = `${project.title} | Zenera`;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = project.description;
  }, [project]);

  if (!project)
    return (
      <NotFound
        title="Project not found"
        description="That project could not be found. Browse our portfolio to explore the work."
      />
    );
  const moreProjects = projects
    .filter((item) => item.slug !== project.slug)
    .slice(0, 3);

  return (
    <div>
      <Navbar />
      <div className="hidden h-[80px] bg-[#F7F7F7] md:block" />
      <header className="relative h-[300px] overflow-hidden bg-secondary md:h-[480px]">
        {project.image && (
          <img
            src={project.image}
            alt={`${project.title} project`}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-black/10" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-10 text-white md:px-8 md:pb-16">
          {/* <p className="mb-3 text-sm font-semibold uppercase tracking-[0.16em]">{project.category} · Sample case study</p> */}
          <h1 className="max-w-3xl text-4xl font-bold md:text-6xl">
            {project.title}
          </h1>
          <p className="mt-4 max-w-2xl text-lg">{project.description}</p>
        </div>
      </header>
      <main className="container mx-auto px-5 py-14 md:px-8 md:py-20">
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 font-semibold text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
          <ArrowLeft size={18} /> Back to portfolio
        </Link>
        <dl className="my-10 grid grid-cols-2 gap-6 border-y border-gray-200 py-6 md:grid-cols-4">
          {[
            ["Client", project.client],
            ["Industry", project.industry],
            // ["Category", project.category],
            ["Year", project.year],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs font-semibold uppercase tracking-wider text-secondary/55">
                {label}
              </dt>
              <dd className="mt-2 font-semibold text-secondary">
                {value || "—"}
              </dd>
            </div>
          ))}
        </dl>
        <div className="grid gap-12 lg:grid-cols-[1fr_300px] lg:gap-20">
          <div className="space-y-10">
            {[
              ["Overview", project.overview],
              // ["The challenge", project.challenge],
              // ["Our approach", project.solution],
            ].map(([heading, copy]) => (
              <section key={heading}>
                <h2 className="mb-4 text-2xl font-semibold text-secondary">
                  {heading}
                </h2>
                <p className="max-w-3xl text-lg leading-relaxed text-secondary/75">
                  {copy}
                </p>
              </section>
            ))}
            {/* <section>
              <h2 className="mb-4 text-2xl font-semibold text-secondary">
                Outcomes
              </h2>
              <ul className="space-y-3">
                {(project.results || []).map((result) => (
                  <li key={result} className="flex gap-3 text-secondary/75">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    {result}
                  </li>
                ))}
              </ul>
            </section> */}
          </div>
          <aside className="h-fit bg-[#F7F7F7] p-7">
            <h2 className="text-lg font-semibold text-secondary">
              Services delivered
            </h2>
            <ul className="mt-4 space-y-3">
              {(project.services || []).map((service) => (
                <li key={service} className="text-secondary/75">
                  {service}
                </li>
              ))}
            </ul>
          </aside>
        </div>
        <section className="mt-20 border-t border-gray-200 pt-12">
          <div className="mb-8 flex items-end justify-between">
            <h2 className="text-3xl font-semibold text-secondary">
              More projects
            </h2>
            <Link
              to="/portfolio"
              className="hidden items-center gap-1 font-semibold text-primary hover:underline sm:flex"
            >
              All projects <ArrowUpRight size={17} />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {moreProjects.map((item) => (
              <Link
                key={item.id}
                to={`/portfolio/${item.slug}`}
                className="group"
              >
                <img
                  src={item.image}
                  alt=""
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                {/* <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-primary">
                  {item.category}
                </p> */}
                <h3 className="mt-3 text-xl font-semibold text-secondary group-hover:text-primary">
                  {item.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
